import { CSSResultGroup, LitElement, TemplateResult } from "lit";
import { customElement, property, query, queryAll } from "lit/decorators.js";
import { repeatRuleFormTemplate } from "./repeatRuleForm.html";
import { TanStackFormController } from "@tanstack/lit-form";
import { repeatRuleFormStyles } from "./repeatRuleForm.css";
import { getDefaultRecurrenceRule, RecurrenceRule } from "../../types/recurrenceRule";
import { provideDatepickerStyles } from "../../utils/provideDatepickerStyles";
import { providePrimaryButtonsStyles } from "../../utils/providePrimaryButtonStyles";
import { convertDateToUtc } from "../../utils/convertDateToUtc";
import moment from "moment";
import { provideRadioStyles } from "../../utils/provideRadioStyles";
import { Select } from "@freshworks/crayons/dist";

const DATE_REGEX = /^\d\d\.\d\d\.\d\d\d\d$/;

type FormValues = {
    rule: RecurrenceRule;
};

/** Repeat rule form component. */
@customElement("repeat-rule-form")
export class RepeatRuleForm extends LitElement {

    /** Form instance. */
    protected form = new TanStackFormController(this, {
        defaultValues: {
            rule: getDefaultRecurrenceRule(),
        } as FormValues,
        onSubmit: ({ value }) => {
            this.dispatchEvent(new CustomEvent<RecurrenceRule>("repeatRuleFormSubmit", {
                detail: value.rule,
            }));
        },
    });

    private _initialValues: RecurrenceRule;

    /** Initial values. */
    @property({ attribute: false })
    public set initialValues(value: RecurrenceRule) {
        if (value == null) {
            return;
        }
        this._initialValues = value;
        this.form.api.reset({
            rule: value,
        });
    }

    /** Initial values. */
    public get initialValues(): RecurrenceRule {
        return this._initialValues;
    }

    private _startDate: Date;

    /** Start date. */
    @property({ attribute: false })
    public set startDate(value: Date) {
        if (value == null) {
            return;
        }
        this._startDate = value;
        const currentEndDate = this.form.api.getFieldValue("rule.end.date");

        if (moment(currentEndDate).isSameOrBefore(value, "day")) {
            this.form.api.getFieldInfo("rule.end.date").instance?.setValue(moment(value).add(1, "day")
                .toDate());
        }
    }

    /** Start date. */
    public get startDate(): Date {
        return this._startDate;
    }

    @property({ type: Boolean })
    public accessor disabled = false;

    @property({ attribute: false })
    public accessor onRecurrenceTypeChange: (() => void) | undefined;

    @query(".datepicker")
    public accessor datepicker: HTMLElement | null;

    @query(".submit-button")
    private accessor submitButton: HTMLElement;

    @queryAll("fw-radio")
    private accessor radioButtons: NodeListOf<HTMLElement>;

    @query(".recurrence-select")
    private accessor recurrenceSelect: Select;

    public constructor() {
        super();
    }

    /** @inheritdoc */
    public static readonly styles: CSSResultGroup = repeatRuleFormStyles;

    /** @inheritdoc */
    public render(): TemplateResult {
        return repeatRuleFormTemplate.call(this);
    }

    /** @inheritdoc */
    public firstUpdated(): void {
        providePrimaryButtonsStyles([this.submitButton]);
    }

    /** @inheritdoc */
    public updated(): void {
        if (this.datepicker !== null) {
            provideDatepickerStyles(this.datepicker);
        }
        this.radioButtons.forEach(node => provideRadioStyles(node));
    }

    /** Handles recurrence type change. */
    public async handleRecurrenceTypeChange(): Promise<void> {
        const isLastUpdate = await this.updateComplete;
        if (isLastUpdate) {
            const children = this.renderRoot.querySelectorAll("*");
            await Promise.all([...children].map(child => {
                if ("componentOnReady" in child && typeof child.componentOnReady === "function") {
                    return (child.componentOnReady as () => Promise<void>)();
                }
                return new Promise<void>(resolve => {
                    resolve();
                });
            }));
            this.onRecurrenceTypeChange?.();
            return;
        }
        void this.handleRecurrenceTypeChange();
    }

    /**
     * Handles form submit.
     * @param event Submit event.
     */
    protected async handleSubmit(event: SubmitEvent): Promise<void> {
        event.preventDefault();
        event.stopPropagation();
        await this.form.api.handleSubmit();
    }

    /** Handles cancel button click. */
    protected handleCancelButtonClick(): void {
        this.dispatchEvent(new CustomEvent("repeatRuleFormCancel"));
    }

    /**
     * Handles date change.
     * @param value Value.
     */
    protected handleDateChange(value: string): void {
        if (!DATE_REGEX.test(value)) {
            return;
        }

        const [day, month, year] = value.split(".");

        const oldValue = this.form.api.state.values.rule.end.date;
        const newValue = new Date(oldValue);
        newValue.setFullYear(Number(year), Number(month) - 1, Number(day));
        newValue.setHours(0, 0, 0, 0);

        const date = moment(newValue).subtract(1, "day")
            .endOf("day")
            .toDate();

        this.form.api.getFieldInfo("rule.end.date").instance?.handleChange(convertDateToUtc(date));
    }
}
